import { DateTime } from "luxon";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { NOT_SET_KEY } from "../../utils/facetFilters";
import { EmploymentType } from "../../utils/models";
import { EMPLOYMENT_TYPE_LABEL_KEY } from "../../utils/utilFunctions";
import { DayType } from "./pbjFacets";

const PAY_TYPE_LABEL_KEY: Readonly<Record<string, string>> = {
	"1": "pbj_model.staffing.pay_type.exempt",
	"2": "pbj_model.staffing.pay_type.non_exempt",
	"3": "pbj_model.staffing.pay_type.contract",
};

const isEmploymentType = (value: string): value is EmploymentType => (Object.values(EmploymentType) as string[]).includes(value);

export type PbjFacetLabels = {
	raw: (key: string) => string;
	payType: (key: string) => string;
	employmentType: (key: string) => string;
	month: (key: string) => string;
	dayType: (key: string) => string;
};

/** Facet option labels shared by the PBJ grids; an unrecognized code shows as-is. */
export function useFacetLabels(): PbjFacetLabels {
	const { t, i18n } = useTranslation();
	return useMemo(() => {
		const notSet = t("pbj_model.filter.not_set");
		const raw = (key: string) => (key === NOT_SET_KEY ? notSet : key);
		return {
			raw,
			payType: (key) => (key in PAY_TYPE_LABEL_KEY ? t(PAY_TYPE_LABEL_KEY[key]) : raw(key)),
			employmentType: (key) => (isEmploymentType(key) ? t(EMPLOYMENT_TYPE_LABEL_KEY[key]) : raw(key)),
			month: (key) => {
				const month = DateTime.fromFormat(key, "yyyy-MM", { locale: i18n.language });
				return month.isValid ? month.toFormat("LLLL yyyy") : raw(key);
			},
			dayType: (key) => {
				if (key === DayType.Weekend) return t("pbj_model.staffing.summary.weekend");
				if (key === DayType.Weekday) return t("pbj_model.staffing.summary.weekday");
				return raw(key);
			},
		};
	}, [t, i18n.language]);
}
